// src/pages/submit-app/AllAppsAdmin.tsx
import React, { useEffect, useState, useRef } from 'react';
import { FaEye, FaEyeSlash, FaBars } from 'react-icons/fa';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getAllApps, updateApp } from '../../services/appRegistryService';
import { uploadAppScreenshot, uploadAppIcon, deleteAppAsset } from '../../services/appAssetUploadService';
import type { AppRegistryEntry, AppCategory, AppStatus } from '../../types/appRegistry';
import { APP_CATEGORIES, APP_CATEGORY_LABELS } from '../../types/appRegistry';

const STATUS_STYLES: Record<AppStatus, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  changes_requested: { label: 'Changes Requested', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
};

type EditForm = {
  name: string;
  description: string;
  url: string;
  category: AppCategory;
  iconColor: string;
};

export default function AllAppsAdmin() {
  const [apps, setApps] = useState<AppRegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingScreenshot, setUploadingScreenshot] = useState<string | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState<string | null>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [filterStatus, setFilterStatus] = useState<AppStatus | 'all'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);

  // Native drag state
  const [draggingAppId, setDraggingAppId] = useState<string | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<AppCategory | null>(null);

  const loadApps = () => {
    setLoading(true);
    getAllApps()
      .then((result) => {
        result.sort((a, b) => a.name.localeCompare(b.name));
        setApps(result);
      })
      .catch((err) => console.error('Failed to load apps:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadApps(); }, []);

  const startEdit = (app: AppRegistryEntry) => {
    setEditingId(app.id);
    setEditForm({
      name: app.name,
      description: app.description,
      url: app.url,
      category: app.category,
      iconColor: app.iconColor,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async () => {
    if (!editingId || !editForm) return;
    setSaving(true);
    try {
      await updateApp(editingId, editForm);
      setApps((prev) =>
        prev.map((a) => (a.id === editingId ? { ...a, ...editForm } : a))
      );
      cancelEdit();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleScreenshotUpload = async (appId: string, file: File) => {
    setUploadingScreenshot(appId);
    try {
      const url = await uploadAppScreenshot(appId, file);
      setApps((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, screenshotUrl: url } : a))
      );
    } catch (err) {
      console.error('Screenshot upload failed:', err);
    } finally {
      setUploadingScreenshot(null);
    }
  };

  const handleIconUpload = async (appId: string, file: File) => {
    setUploadingIcon(appId);
    try {
      const url = await uploadAppIcon(appId, file);
      setApps((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, customIconUrl: url } : a))
      );
    } catch (err) {
      console.error('Icon upload failed:', err);
    } finally {
      setUploadingIcon(null);
    }
  };

  const handleDeleteAsset = async (appId: string, field: 'screenshotUrl' | 'customIconUrl', currentUrl: string) => {
    try {
      await deleteAppAsset(appId, field, currentUrl);
      setApps((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, [field]: null } : a))
      );
    } catch (err) {
      console.error('Delete asset failed:', err);
    }
  };

  const toggleGloballyHidden = async (app: AppRegistryEntry) => {
    const newVal = !app.globallyHidden;
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, globallyHidden: newVal } : a)));
    try {
      await updateApp(app.id, { globallyHidden: newVal });
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
      setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, globallyHidden: !newVal } : a)));
    }
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredApps = filterStatus === 'all' ? apps : apps.filter((a) => a.status === filterStatus);

  const appsByCategory = APP_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = filteredApps.filter((a) => a.category === cat);
    return acc;
  }, {} as Record<AppCategory, AppRegistryEntry[]>);

  const allFilteredSelected = filteredApps.length > 0 && filteredApps.every((a) => selected.has(a.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const a of filteredApps) next.delete(a.id);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const a of filteredApps) next.add(a.id);
        return next;
      });
    }
  };

  const bulkSetHidden = async (hidden: boolean) => {
    const ids = [...selected].filter((id) => {
      const app = apps.find((a) => a.id === id);
      return app && app.globallyHidden !== hidden;
    });
    if (ids.length === 0) return;
    setBulkSaving(true);
    setApps((prev) => prev.map((a) => (ids.includes(a.id) ? { ...a, globallyHidden: hidden } : a)));
    try {
      await Promise.all(ids.map((id) => updateApp(id, { globallyHidden: hidden })));
    } catch (err) {
      console.error('Bulk update failed:', err);
      loadApps();
    } finally {
      setBulkSaving(false);
      setSelected(new Set());
    }
  };

  // --- Native drag-and-drop handlers ---
  const handleDragStart = (e: React.DragEvent, appId: string) => {
    setDraggingAppId(appId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appId);
    // Make the drag image semi-transparent
    if (e.currentTarget instanceof HTMLElement) {
      requestAnimationFrame(() => {
        (e.currentTarget as HTMLElement).style.opacity = '0.4';
      });
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggingAppId(null);
    setDragOverCategory(null);
  };

  const handleDragOver = (e: React.DragEvent, cat: AppCategory) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategory(cat);
  };

  const handleDragLeave = (e: React.DragEvent, cat: AppCategory) => {
    // Only clear if we're actually leaving this zone (not entering a child)
    if (dragOverCategory === cat && !e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverCategory(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetCategory: AppCategory) => {
    e.preventDefault();
    const appId = e.dataTransfer.getData('text/plain');
    setDraggingAppId(null);
    setDragOverCategory(null);

    const app = apps.find((a) => a.id === appId);
    if (!app || app.category === targetCategory) return;

    // Optimistic update
    setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, category: targetCategory } : a)));
    try {
      await updateApp(appId, { category: targetCategory });
    } catch (err) {
      console.error('Failed to update category:', err);
      setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, category: app.category } : a)));
    }
  };

  const draggingApp = draggingAppId ? apps.find((a) => a.id === draggingAppId) : null;

  const renderAppRow = (app: AppRegistryEntry) => {
    const isEditing = editingId === app.id;
    const statusInfo = STATUS_STYLES[app.status];

    if (isEditing && editForm) {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 dark:text-white">Editing: {app.name}</h3>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusInfo.className}`}>
              {statusInfo.label}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name *</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">URL *</label>
              <input
                type="url"
                value={editForm.url}
                onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category</label>
              <select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value as AppCategory })}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
              >
                {APP_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{APP_CATEGORY_LABELS[cat]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Card Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={editForm.iconColor}
                  onChange={(e) => setEditForm({ ...editForm, iconColor: e.target.value })}
                  className="h-8 w-8 rounded border border-gray-300 cursor-pointer"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">{editForm.iconColor}</span>
              </div>
            </div>
          </div>

          {/* Image uploads */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            {/* Screenshot */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Screenshot</label>
              {app.screenshotUrl ? (
                <div className="space-y-1">
                  <img src={app.screenshotUrl} alt="Screenshot" className="w-full max-h-24 object-cover rounded border border-gray-200 dark:border-gray-700" />
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => screenshotInputRef.current?.click()}>Replace</Button>
                    <Button variant="outline" size="sm" className="text-xs h-7 text-red-500 hover:text-red-600" onClick={() => handleDeleteAsset(app.id, 'screenshotUrl', app.screenshotUrl!)}>Remove</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" className="text-xs h-7" disabled={uploadingScreenshot === app.id} onClick={() => screenshotInputRef.current?.click()}>
                  {uploadingScreenshot === app.id ? 'Uploading...' : 'Upload Screenshot'}
                </Button>
              )}
              <input ref={screenshotInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleScreenshotUpload(app.id, file); e.target.value = ''; }} />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Custom Icon</label>
              {app.customIconUrl ? (
                <div className="space-y-1">
                  <img src={app.customIconUrl} alt="Icon" className="h-12 w-12 rounded border border-gray-200 dark:border-gray-700" />
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => iconInputRef.current?.click()}>Replace</Button>
                    <Button variant="outline" size="sm" className="text-xs h-7 text-red-500 hover:text-red-600" onClick={() => handleDeleteAsset(app.id, 'customIconUrl', app.customIconUrl!)}>Remove</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" className="text-xs h-7" disabled={uploadingIcon === app.id} onClick={() => iconInputRef.current?.click()}>
                  {uploadingIcon === app.id ? 'Uploading...' : 'Upload Icon'}
                </Button>
              )}
              <input ref={iconInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleIconUpload(app.id, file); e.target.value = ''; }} />
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={saveEdit} disabled={saving || !editForm.name || !editForm.url}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" size="sm" onClick={cancelEdit}>Cancel</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Drag handle */}
          <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-300 flex-shrink-0">
            <FaBars className="h-3.5 w-3.5" />
          </div>

          <input
            type="checkbox"
            checked={selected.has(app.id)}
            onChange={() => toggleSelected(app.id)}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 accent-blue-600 flex-shrink-0"
          />
          {/* Color swatch / favicon */}
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: app.iconColor || '#334155' }}
          >
            {app.faviconUrl ? (
              <img src={app.faviconUrl} alt="" className="h-5 w-5 rounded" />
            ) : (
              <span className="text-white font-bold text-xs">{app.name.charAt(0)}</span>
            )}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className={`font-medium truncate ${app.globallyHidden ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>{app.name}</h3>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusInfo.className}`}>
                {statusInfo.label}
              </span>
              {app.globallyHidden && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  Hidden
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{app.url}</p>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              by {app.submittedBy}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => toggleGloballyHidden(app)}
            title={app.globallyHidden ? 'Show to all users' : 'Hide from all users'}
            className={`p-1.5 rounded-md transition-colors ${
              app.globallyHidden
                ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/40'
            }`}
          >
            {React.createElement(app.globallyHidden ? FaEyeSlash : FaEye, { className: 'h-4 w-4' })}
          </button>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => startEdit(app)}>
            Edit
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <p className="text-gray-500 dark:text-gray-400">Loading all apps...</p>;
  }

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500 dark:text-gray-400">Filter:</span>
        {(['all', 'approved', 'pending', 'rejected', 'changes_requested'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              filterStatus === s
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-transparent text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-gray-400'
            }`}
          >
            {s === 'all' ? `All (${apps.length})` : `${STATUS_STYLES[s].label} (${apps.filter((a) => a.status === s).length})`}
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {filteredApps.length > 0 && (
        <div className="flex items-center gap-3 py-2 px-3 rounded-lg bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 accent-blue-600"
            />
            {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
          </label>
          {selected.size > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1.5"
                disabled={bulkSaving}
                onClick={() => bulkSetHidden(true)}
              >
                {React.createElement(FaEyeSlash, { className: 'h-3 w-3' })}
                Hide Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1.5"
                disabled={bulkSaving}
                onClick={() => bulkSetHidden(false)}
              >
                {React.createElement(FaEye, { className: 'h-3 w-3' })}
                Show Selected
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Sticky quick-drop bar — visible only while dragging */}
      <div
        className={`sticky top-0 z-50 transition-all duration-200 ${
          draggingAppId
            ? 'opacity-100 max-h-24 py-2 px-3 mb-2 bg-gray-900/95 backdrop-blur border border-gray-700 rounded-lg shadow-lg'
            : 'opacity-0 max-h-0 overflow-hidden pointer-events-none'
        }`}
      >
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 text-center">
          Drop <strong className="text-gray-300">{draggingApp?.name}</strong> into category
        </p>
        <div className="grid grid-cols-4 gap-2">
          {APP_CATEGORIES.map((cat) => (
            <div
              key={cat}
              onDragOver={(e) => handleDragOver(e, cat)}
              onDragLeave={(e) => handleDragLeave(e, cat)}
              onDrop={(e) => handleDrop(e, cat)}
              className={`text-center py-2.5 rounded-md border-2 border-dashed text-xs font-semibold transition-all select-none ${
                dragOverCategory === cat
                  ? 'border-blue-400 bg-blue-500/20 text-blue-300 scale-105'
                  : draggingApp?.category === cat
                    ? 'border-gray-600 bg-gray-800/50 text-gray-500'
                    : 'border-gray-600 bg-gray-800/30 text-gray-400'
              }`}
            >
              {APP_CATEGORY_LABELS[cat]}
              {draggingApp?.category === cat && (
                <span className="block text-[9px] font-normal text-gray-500 mt-0.5">current</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Category sections */}
      {APP_CATEGORIES.map((cat) => {
        const catApps = appsByCategory[cat];
        const isDropTarget = dragOverCategory === cat;
        return (
          <div
            key={cat}
            className="space-y-2"
            onDragOver={(e) => handleDragOver(e, cat)}
            onDragLeave={(e) => handleDragLeave(e, cat)}
            onDrop={(e) => handleDrop(e, cat)}
          >
            <div className="flex items-center gap-2 pt-2">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                {APP_CATEGORY_LABELS[cat]}
              </h2>
              <span className="text-xs text-gray-400 dark:text-gray-500">({catApps.length})</span>
            </div>

            <div
              className={`space-y-2 min-h-[48px] rounded-lg border-2 border-dashed p-2 transition-colors ${
                isDropTarget
                  ? 'border-blue-400 bg-blue-500/10'
                  : catApps.length === 0
                    ? 'border-gray-300 dark:border-gray-700'
                    : 'border-transparent'
              }`}
            >
              {catApps.length === 0 && !isDropTarget && (
                <p className="text-xs text-gray-400 dark:text-gray-600 text-center py-2">
                  Drop apps here
                </p>
              )}
              {isDropTarget && catApps.length === 0 && (
                <p className="text-xs text-blue-400 text-center py-2 font-medium">
                  Release to move here
                </p>
              )}
              {catApps.map((app) => (
                <div
                  key={app.id}
                  draggable={editingId !== app.id}
                  onDragStart={(e) => handleDragStart(e, app.id)}
                  onDragEnd={handleDragEnd}
                >
                  <Card className={draggingAppId === app.id ? 'opacity-40 ring-2 ring-blue-400' : ''}>
                    <CardContent className="py-4">
                      {renderAppRow(app)}
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
